import { assertValue } from '@shared/helpers'
import { LoanOfferGetLoanOfferResponse, LoanOfferItem, LoanOfferMode } from '@shared/sec-loans'
import { GetDataDoneEvent } from '@ui-common/steps/GetDataStep'
import { type NextStepEvent as CommonNextStepEvent, PreviousStepEvent } from '@ui-common/types'
import { assign, setup } from 'xstate'

import { AuthenticatedContext, User } from '../../common/types'
import { LoanOfferWizardData } from './types'

export enum WizardStep {
  InitialLoanOfferStep = 'initialLoanOfferStep',
  GetLoanOfferStep = 'getLoanOfferStep',
  StatusStep = 'statusStep',
  OfferStep = 'offerStep',
  InstallmentStep = 'installmentStep',
  TermsInfoStep = 'termsInfoStep',
  RecommendationStep = 'recommendationStep',
  BookingStep = 'bookingStep',
  TheEndStep = 'theEndStep',
}

export type WizardContext = Partial<AuthenticatedContext> & {
  user: User
  loanOfferId: string
  loanOffer?: LoanOfferItem
  wizardData?: LoanOfferWizardData
}

export type WizardMachineType = ReturnType<typeof createWizardMachine>

type BackToStatusEvent = { type: 'BACK_TO_STATUS' }
type GetApplicationEvent = GetDataDoneEvent<LoanOfferGetLoanOfferResponse>
type NextStepEvent = CommonNextStepEvent<WizardContext['wizardData']>

type WizardEvent = GetApplicationEvent | NextStepEvent | PreviousStepEvent | BackToStatusEvent

const saveWizardDataAction = {
  type: 'saveFormData',
  params: ({ event }: { event: NextStepEvent }) => assertValue(event.data, 'event.data'),
} as const

// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
export const createWizardMachine = () =>
  setup({
    types: {
      context: {} as WizardContext,
      events: {} as WizardEvent,
      input: {} as WizardContext,
    },
    actions: {
      saveLoanOffer: assign(({ context }, loanOffer: LoanOfferItem) => {
        return {
          ...context,
          loanOffer,
        }
      }),
      saveFormData: assign(({ context }, wizardData: Partial<LoanOfferWizardData>) => {
        return {
          ...context,
          wizardData: {
            ...context.wizardData,
            ...wizardData,
          },
        }
      }),
    },
    guards: {
      hasLoanOfferItem: ({ context }) => {
        return !!context.loanOffer
      },
      shouldGoBookingStep: ({ context }) => {
        const loanOffer = assertValue(context.loanOffer, 'context.loanOffer')
        return loanOffer.loanOfferMode === LoanOfferMode.Automatic && !!loanOffer.reservationLink
      },
    },
  }).createMachine({
    id: 'loanOfferWizardMachine',
    initial: WizardStep.InitialLoanOfferStep,
    context: ({ input }) => ({ ...input }),
    on: {
      BACK_TO_STATUS: { target: `.${WizardStep.StatusStep}` },
    },
    states: {
      [WizardStep.InitialLoanOfferStep]: {
        always: [
          {
            guard: { type: 'hasLoanOfferItem' },
            target: WizardStep.StatusStep,
          },
          {
            target: WizardStep.GetLoanOfferStep,
          },
        ],
      },
      [WizardStep.GetLoanOfferStep]: {
        on: {
          GET_DATA_DONE: [
            {
              actions: {
                type: 'saveLoanOffer',
                params: ({ event }: { event: GetApplicationEvent }) => event.data,
              },
              target: WizardStep.StatusStep,
            },
          ],
        },
      },
      [WizardStep.StatusStep]: {
        id: WizardStep.StatusStep,
        on: {
          NEXT_STEP: { target: WizardStep.OfferStep },
        },
      },
      [WizardStep.OfferStep]: {
        id: WizardStep.OfferStep,
        on: {
          PREVIOUS_STEP: { target: WizardStep.StatusStep },
          NEXT_STEP: { target: WizardStep.InstallmentStep },
        },
      },
      [WizardStep.InstallmentStep]: {
        id: WizardStep.InstallmentStep,
        on: {
          PREVIOUS_STEP: { target: WizardStep.OfferStep },
          NEXT_STEP: { target: WizardStep.TermsInfoStep },
        },
      },
      [WizardStep.TermsInfoStep]: {
        id: WizardStep.TermsInfoStep,
        on: {
          PREVIOUS_STEP: { target: WizardStep.InstallmentStep },
          NEXT_STEP: { target: WizardStep.RecommendationStep },
        },
      },
      [WizardStep.RecommendationStep]: {
        id: WizardStep.RecommendationStep,
        on: {
          PREVIOUS_STEP: { target: WizardStep.TermsInfoStep },
          NEXT_STEP: [
            { guard: 'shouldGoBookingStep', target: WizardStep.BookingStep, actions: [saveWizardDataAction] },
            { target: WizardStep.TheEndStep, actions: [saveWizardDataAction] },
          ],
        },
      },
      [WizardStep.BookingStep]: {
        id: WizardStep.BookingStep,
        on: {
          PREVIOUS_STEP: { target: WizardStep.RecommendationStep },
        },
      },
      [WizardStep.TheEndStep]: {
        id: WizardStep.TheEndStep,
        on: {
          PREVIOUS_STEP: { target: WizardStep.RecommendationStep },
        },
      },
    },
  })
